<script lang="ts">
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import Fa from 'svelte-fa'
    import { faPaperPlane, faAngleLeft, faCopy, faDownload, faLink, faPrint, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { FormData } from '../../modules/toolsApi/forms/form-datas/form-data'
    import type { FormTemplate } from '../../modules/toolsApi/forms/form-templates/form-template'
    import {
        FormTemplateItemType,
        FormTemplateTypeEnum,
        FormTemplateTypeFriendlyEnum,
    } from '../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'
    import type { CreateFormDataDto } from '../../modules/toolsApi/forms/form-datas/dto/create-form-data.dto'

    import { UpdateFormDataDto } from '../../modules/toolsApi/forms/form-datas/dto/update-form-data.dto'
    import CustomerServiceContent from './elements/customer-service-content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Content from '../_common/content.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import { FormDataStatusEnum } from './form-data-status.enum'
    import Zact from '../_common/z/zact.svelte'
    import type { User } from '../../modules/usersApi/users/user'
    import Zinput from '../_common/z/zinput.svelte'
    import type { Invoice } from '../../modules/posApi/invoices/invoice'
    import type { BillEntity } from '../../modules/financialApi/bills/bill'
    import { SendMessageTemplate } from '../_common/send-message.template'
    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    let invoiceRequired = false

    let formData: FormData = new FormData()
    let formTemplates: FormTemplate[] = []

    export let personId: string = null
    export let formDataId: string = null
    export let invoiceId: string = null
    export let billId: string = null
    export let view: boolean
    export let types: FormTemplateTypeEnum[] = []

    let company = window.store?.company
    let invoice: Invoice = null
    let bill: BillEntity = null

    let draft = false
    let changed = false
    let step = 1

    async function loadInvoice(invoiceId) {
        if (!invoiceId) return
        invoice = await window.api.pos.invoices.getOne(invoiceId)
        invoice.services = await window.api.utils.utils.assignServicesFromObject(invoice.services)
        invoice.services = await window.api.utils.utils.assignUsersFromObject(invoice.services)
        console.log('invoice_services', invoice.services)
        invoice = invoice
        await tick()
        invoice = (await window.api.utils.utils.assignUsersFromObject([invoice]))[0]
        await tick()
        formData.document = invoice
        formData.documentType = 'invoice'
        formData = formData
    }

    async function loadBill(billId) {
        if (!billId) return
        bill = await window.api.financials.bills.getOne(billId)
        bill = await window.api.utils.utils.assignPersonsFromObject(bill)
        formData.document = bill
        formData.documentType = 'bill'
        formData = formData
    }

    async function invoiceSelectOnChange() {
        invoice = (await window.api.utils.utils.assignUsersFromObject([invoice]))[0]
        invoice.services = await window.api.utils.utils.assignUsersFromObject(invoice.services)
        formData.document = invoice
        formData.documentType = 'invoice'
        formData = formData
    }

    async function loadFormTemplates() {
        formTemplates = await window.api.tools.forms.formTemplates.getAll()
        formTypeOnChange()
    }

    async function editEntity(id: string) {
        try {
            show = true
            formData = new FormData()
            formData = await window.api.tools.forms.formDatas.getOne(id)
            formData.person = await window.api.events.persons.getOne(formData.personId)
            formData.attendant = await window.api.users.users.getOne(formData.attendantId)
            formData = formData
            if (formData.invoiceId) formData.documentType = 'invoice'
            if (formData.documentType === 'invoice') await loadInvoice(formData.invoiceId || formData.documentId)
            if (formData.documentType === 'bill') await loadBill(formData.documentId)
            invoiceRequired = !!invoice
            formTemplateId = formData.formTemplateId
            changed = false
            console.log('form', formData)
            await tick()
            dispatch('load')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        formData = new FormData()
        formData.document = null
        formData.createdAt = new Date()
        if (personId) {
            formData.person = personId ? await window.api.events.persons.getOne(personId) : null
            formData.personId = formData.person?.personId
        }
        changed = false
    }

    function replaceTagsFromTemplate(template: FormTemplate) {
        for (let item of template.form) {
            if (item.type === FormTemplateItemType.label_formated) {
                item.content = [
                    window.api.utils.utils.formsReplaceTags(
                        item.content[0],
                        formData.person,
                        invoice || bill,
                        formData.attendant,
                        formData.createdAt,
                        formData.documentType,
                    ),
                ]
            }
        }
        return template
    }

    function getDocumentType() {
        if (invoice) return 'invoice'
        if (billId) return 'bill'
        return undefined
    }

    function getDocumentId() {
        if (invoice) return invoice.invoiceId
        if (billId) return billId
        return undefined
    }

    async function createEntity(finished: boolean) {
        try {
            if (show) return
            show = true
            draft = false
            const dto: CreateFormDataDto = {
                documentId: getDocumentId(),
                documentType: getDocumentType(),
                personId: formData.person?.personId,
                attendantId: formData.attendant?.userId,
                invoiceId: invoice?.invoiceId,
                formTemplateId: formData.formTemplateId,
                content: formData.content,
                template: replaceTagsFromTemplate(formData.template),
                status: finished ? FormDataStatusEnum.ACTIVE : FormDataStatusEnum.TEMPORARY,
                accessRead: formData.accessRead,
            }
            if (!validateCreateDto(dto, finished)) {
                show = false
                return false
            }
            if (draft) dto.status = FormDataStatusEnum.TEMPORARY
            formData = await window.api.tools.forms.formDatas.create(dto)
            if (finished) dispatchChange()
            show = false
            changed = false
            return true
        } catch (error) {
            evaluateError(error)
            show = false
            return false
        } finally {
        }
    }

    async function updateEntity(finished: boolean) {
        if (formData.status === FormDataStatusEnum.SIGNED) return true
        try {
            if (show) return
            show = true
            draft = false
            const dto: UpdateFormDataDto = {
                content: formData.content,
                status: finished || formData.status === FormDataStatusEnum.ACTIVE ? FormDataStatusEnum.ACTIVE : FormDataStatusEnum.TEMPORARY,
                accessRead: formData.accessRead,
            }
            if (!validateUpdateDto(dto, finished)) {
                show = false
                return false
            }
            if (draft) dto.status = FormDataStatusEnum.TEMPORARY
            await window.api.tools.forms.formDatas.update(formData.formDataId, dto)
            if (finished) dispatchChange()
            show = false
            changed = false
            return true
        } catch (error) {
            evaluateError(error)
            show = false
            return false
        } finally {
        }
    }

    function checkDocumentSigned(dto: CreateFormDataDto | UpdateFormDataDto) {
        for (const item of dto.content) {
            if (item.type === 'sign') {
                if (item?.value?.includes('https:') || item.value.length >= 1000) {
                    return true
                }
            }
        }
        return false
    }

    function checkRequiredFields(dto: CreateFormDataDto | UpdateFormDataDto) {
        for (const item of dto.content) {
            let field = formData.template.form.find((el) => el.id === item.id)
            if (field.type === 'label' || field.type === 'label_formated') continue
            if (field && field.required) {
                if (!item.another) {
                    if (Array.isArray(item.value)) {
                        if (item.value.length === 0) {
                            return false
                        }
                    } else {
                        if (!item.value) {
                            return false
                        }
                    }
                }
            } else if (item.type === 'sign') {
                if (!item?.value?.includes('https:') && item.value.length < 1000) {
                    return false
                }
            }
        }
        return true
    }

    function translateField(field: string) {
        if (field === 'document') return 'CPF/CNPJ'
        if (field === 'email') return 'E-mail'
        if (field === 'address') return 'Endereço'
        return field
    }

    function validateCreateDto(dto, finished: boolean) {
        if (!dto.formTemplateId) {
            evaluateError('Selecione o formulário')
            return false
        }
        if (!dto.attendantId) {
            evaluateError('Selecione o atendente')
            return false
        }
        if (!dto.personId) {
            evaluateError('Selecione o cliente')
            return false
        }
        if (invoiceRequired) {
            if (!dto.invoiceId && !dto.documentId) {
                evaluateError('Selecione a venda realizada')
                return false
            }
        }

        if (formData.template.requiredFields?.length > 0) {
            for (const field of formData.template.requiredFields) {
                console.log('field', field)
                if (!formData.person[field]) {
                    evaluateError('Para emitir este documento complete o cadastro do cliente:\n' + translateField(formData.template.requiredFields.join(',')))
                    return
                }
            }
        }

        if (checkDocumentSigned(dto)) {
            if (!confirm('O documento foi assinado.\nAo salvar ele não poderá mais ser editado.\nDeseja mesmo salvar?')) return false
        }

        if (!finished) return true // não passa por validação se é salvamento do rascunho
        if (dto.content.length === 0) {
            evaluateError('Formulário sem informações')
            return false
        }

        if (!checkRequiredFields(dto)) {
            if (!confirm('Nem todos os campos foram preenchidos. Deseja salvar como rascunho?')) return false
            draft = true
        }

        return true
    }

    function validateUpdateDto(dto, finished: boolean) {
        if (checkDocumentSigned(dto)) {
            if (!confirm('O documento foi assinado.\nAo salvar ele não poderá mais ser editado.\nDeseja mesmo salvar?')) return false
        }
        if (!finished) return true
        if (dto.content.length === 0) {
            evaluateError('Formulário sem informações')
            return false
        }

        if (!checkRequiredFields(dto)) {
            if (!confirm('Nem todos os campos foram preenchidos. Deseja salvar como rascunho?')) return false
            draft = true
        }
        return true
    }

    async function saveEntity(finished) {
        if (formData.formDataId) {
            return await updateEntity(finished)
        } else {
            return await createEntity(finished)
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.tools.forms.formDatas.remove(formData.formDataId)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        if (error === "Cannot read properties of undefined (reading 'personId')") {
            error = 'Selecione ou cadastre o cliente'
        }
        window.error(error)
    }

    let show = false

    function dispatchCancel() {
        if (changed && !confirm('As alterações não foram salvas. Deseja mesmo sair?')) return
        dispatch('cancel')
    }

    function dispatchChange() {
        dispatch('change', formData)
    }

    let link = ''
    let linkLabel = ''
    let linkField
    let linkPage = ''

    function compactFormDataToken(formDataLinkId: string, token: string) {
        return window.api.utils.conversions.uuidToBase58(formDataLinkId) + '_' + window.api.utils.conversions.uuidToBase58(token)
    }

    async function generateLink(send: boolean) {
        console.log('formData', formData)
        for (const item of formData.content) {
            if (item.type === 'sign') {
                item.value = ''
            }
        }
        if (!(await saveEntity(false))) return
        // Link com token dedicado (expira em 3 dias) — id/cid crus foram descontinuados por não terem
        // nenhum segredo, só o próprio formDataId+companyId (ver form-data-link no backend).
        const expiresAt = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
        const linkEntity = await window.api.tools.forms.formDataLinks.create({ formDataId: formData.formDataId, expiresAt })
        linkPage = 'public/customer-service?id=' + compactFormDataToken(linkEntity.formDataLinkId, linkEntity.token)
        link = window.api.utils.utils.domain() + '/' + linkPage
        linkLabel = 'Copiar link'
        if (send) {
            sendMessage()
            dispatchChange()
            show = false
            changed = false
            return
        }
        step = 2
        copied = false
    }

    async function downloadSignedPdf() {
        try {
            show = true
            const { url } = await window.api.tools.forms.formDatas.getSignedDocumentUrl(formData.formDataId)
            window.open(url, '_blank')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let copied = false
    function copyLink() {
        linkField.select()
        linkField.setSelectionRange(0, 99999) /* For mobile devices */
        navigator.clipboard.writeText(linkField.value)
        linkLabel = 'Copiado'
        copied = true
    }

    onMount(async () => {
        show = true
        await loadAccessList()
        await loadFormTemplates()
        if (formDataId) {
            await editEntity(formDataId)
        } else {
            await newEntity()
        }
        show = false
        dispatch('loaded')
    })

    async function checkExternalDocument() {
        invoiceRequired = false
        if (!formData?.template?.form) {
            invoice = null
            formData.invoiceId = null
            formData.documentId = null
            formData.documentType = null
            return
        }
        for (let item of formData.template.form) {
            if (item.type === FormTemplateItemType.label_formated) {
                if (
                    item.content[0].includes('{compra}') ||
                    item.content[0].includes('{compraporextenso}') ||
                    item.content[0].includes('{pagamentoporextenso}') ||
                    item.content[0].includes('{valordacompraporextenso}') ||
                    item.content[0].includes('{valordopagamentoporextenso}') ||
                    item.content[0].includes('{compraporextenso}') ||
                    item.content[0].includes('{datacompra}') ||
                    item.content[0].includes('{pagamento}') ||
                    item.content[0].includes('{databaixa}') ||
                    item.content[0].includes('{descricao}') ||
                    item.content[0].includes('{vendedor}')
                ) {
                    if (invoiceId) await loadInvoice(invoiceId)
                    if (billId) await loadBill(billId)
                    invoiceRequired = true
                    return
                }
            }
        }
        invoice = null
        formData.invoiceId = null
    }

    let formTemplateId: string = ''
    async function formTemplateIdOnChanged() {
        if (changed && !confirm('As alterações não foram salvas. Deseja mesmo sair?')) {
            formTemplateId = formData.formTemplateId
            return
        }
        formData.formTemplateId = formTemplateId
        formData.template = formTemplates.find((el) => el.formTemplateId === formTemplateId)
        checkExternalDocument()
        changed = false
    }

    function customerServiceContentOnChange() {
        changed = true
    }

    let accessList: { label: string; value: string }[] = []
    async function loadAccessList() {
        accessList = []
        const users: User[] = await window.api.users.companies.getCompanyUsers(600)
        accessList = users.map((el) => {
            return {
                label: el.userName,
                value: el.userId,
            }
        })
    }

    async function goPrint() {
        const me = (await import(`./customer-service-form.svelte`)).default
        window.printer({
            component: [me],
            props: [{ personId, formDataId, view }],
            hideWaterMark: true,
            waitLoadedEvent: true,
        })
        return
        //window.print()
        let width = screen.width - 100
        if (width > 1045) width = 1045
        const printer = window.open(
            '',
            'Zandu - Documento',
            'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=' +
                width +
                ',height=' +
                (screen.height - 140) +
                ',top=' +
                50 +
                ',left=' +
                (screen.width / 2 - width / 2),
        )
        printer.location.href = '/customer-service/print?id=' + formDataId
    }

    let formTypes = Object.keys(FormTemplateTypeEnum)
    let formTypesOptions = []
    for (const item of formTypes) {
        formTypesOptions.push({ value: item, label: FormTemplateTypeFriendlyEnum[item] })
    }

    let filteredFormTemplates: FormTemplate[] = []
    function formTypeOnChange() {
        if (!types) types = []
        if (types?.length === 0) {
            filteredFormTemplates = formTemplates
        } else {
            filteredFormTemplates = formTemplates.filter((el) => types.includes(el.type))
        }
    }

    function sendMessage() {
        window.message(SendMessageTemplate.share_link_02(linkPage, 'Documento digital', formData.personId))
    }
</script>

{#if !show}
    <Content>
        <Zact>
            <button class="btn btn-secondary" on:click={dispatchCancel}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer h-mobile" />
            {#if !!formData.formDataId && formData.status !== FormDataStatusEnum.SIGNED && !view}
                <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
            {/if}
            {#if step === 1}
                {#if !view}
                    {#if formData.status !== FormDataStatusEnum.ACTIVE && formData.status !== FormDataStatusEnum.SIGNED}
                        <button class="btn btn-primary" on:click={() => generateLink(false)}
                            ><Fa icon={faLink} /> <span class="h-full">Link</span><span class="h-mobile">Gerar link</span></button
                        >
                        <button class="btn btn-primary" on:click={() => generateLink(true)}><Fa icon={faPaperPlane} /> Enviar</button>
                    {/if}
                    {#if formData.status !== FormDataStatusEnum.SIGNED}
                        <button class="btn btn-primary" on:click={() => saveEntity(true)}><Fa icon={faSave} /> Salvar</button>
                    {/if}
                {:else}
                    <button class="btn btn-secondary h-mobile" on:click={() => goPrint()}>
                        <Fa icon={faPrint} />
                    </button>
                    {#if formData.status === FormDataStatusEnum.SIGNED}
                        <button class="btn btn-secondary" on:click={downloadSignedPdf}><Fa icon={faDownload} /> Baixar PDF</button>
                    {/if}
                {/if}
            {/if}
        </Zact>

        <div class="grid-form cs_new_outer mt-2">
            {#if step === 1}
                <Zframe>
                    <div class="cs_new">
                        {#if !view}
                            <div class="cs_new_menu h-print">
                                <Zinput label="Filtrar documento" type="multiselect" bind:value={types} options={formTypesOptions} on:change={formTypeOnChange}
                                ></Zinput>
                                {#if filteredFormTemplates.length === 0}
                                    <div class="text-muted cs_new_menu_help">Nenhum modelo de atendimento disponível</div>
                                {:else}
                                    <div class="cs_new_menu_models">
                                        <div class="cs_new_menu_models_items">
                                            <div class="cs_new_menu_models_items_item">
                                                <div class="btn-group-vertical">
                                                    {#each filteredFormTemplates as formTemplate, index}
                                                        {#if formTemplate.status === 100}
                                                            <input
                                                                disabled={!!formDataId || view}
                                                                class="btn-check"
                                                                type="radio"
                                                                bind:group={formTemplateId}
                                                                name="g_models"
                                                                id="g_models_{index}"
                                                                value={formTemplate.formTemplateId}
                                                                on:change={formTemplateIdOnChanged}
                                                            />
                                                            <label for="g_models_{index}" class="btn btn-outline-secondary">{formTemplate.title}</label>
                                                        {/if}
                                                    {/each}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <ZentitySelect fonte="usuario" label="Preenchido por" disabled={!!formDataId || view} bind:value={formData.attendant}></ZentitySelect>
                                    <ZentitySelect fonte="pessoa" label="Cliente" disabled={!!formDataId || view} bind:value={formData.person}></ZentitySelect>
                                    {#if invoiceRequired && !invoiceId && !billId}
                                        <ZentitySelect
                                            fonte="venda"
                                            disabled={!!formDataId || view}
                                            label="Venda"
                                            params={{ personId: formData?.person?.personId }}
                                            bind:value={invoice}
                                            on:change={invoiceSelectOnChange}
                                        />
                                    {/if}
                                    <Zinput
                                        label="Usuários com acesso autorizado"
                                        type="multiselect"
                                        bind:value={formData.accessRead}
                                        options={accessList}
                                        help="Caso preenchido, apenas os usuários informados terão acesso ao documento, além do proprietário. Deixe em branco para que todos tenham acesso."
                                    ></Zinput>
                                {/if}
                            </div>
                        {/if}
                        <div class="cs_new_content">
                            {#if formTemplateId}
                                <CustomerServiceContent {company} isLink={false} {view} on:change={customerServiceContentOnChange} bind:formData
                                ></CustomerServiceContent>
                            {:else}
                                <div class="text-muted cs_new_content_help">
                                    Para iniciar um atendimento selecione o cliente e em seguida selecione um modelo de atendimento.
                                </div>
                            {/if}
                        </div>
                    </div>
                </Zframe>
            {/if}
            {#if step === 2}
                <Zframe
                    title="Link para o formulário do cliente"
                    class="mt-3"
                    headline="Atenção: Qualquer pessoa com acesso a este link terá acesso ao conteúdo. Após acessar o link e salvar, este link ficará inacessível."
                >
                    <div class="row mt-2">
                        <div class="col">
                            <div class="input-group input-group-sm">
                                <div class="input-group-text">Link</div>
                                <input bind:this={linkField} type="text" value={link} class="form-control form-control-sm text-center" disabled />
                                <button class="btn btn-primary" on:click={copyLink}><Fa icon={faCopy} /> {linkLabel}</button>
                            </div>
                        </div>
                    </div>
                </Zframe>
            {/if}
        </div>
    </Content>
{/if}
<LoadSpinner delay={0} {show} />

<style>
    .row {
        padding: 0px 16px;
    }
    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
        .grid-act {
            margin-top: 16px;
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .grid-act .btn {
            max-width: 120px;
            flex-grow: 1;
        }
        .add-service {
            text-align: center;
        }
    }
</style>
